import { Injectable, inject } from '@angular/core';
import { Store } from '../store/store.model';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable, of, catchError, debounceTime, distinctUntilChanged, filter, map } from 'rxjs';
import { MapPersistedState } from '../../map/models/map-persisted-state';
import { Garage } from '../../shared/models/garage';
import { VehiclePunctualityConfig, VehicleWarningConfig } from '../../shared/models/vehicle';
import { AppState, APP_CONTEXT } from '../models/app-state';
import { UserMapConfig } from '../models/user-map-config';
import { MapArea } from '../utilities/filter-spaces-models';
import { FilterSpacesService } from '../utilities/filter-spaces.service';
import { TrafficDeviationsConfig } from '@components/traffic-deviations/models/traffic-deviations-config';
import { ConfigService } from 'src/config';
import * as _ from 'lodash';
import { handleError, isEmptyError } from './service.helpers';
import { LoggingService } from '@modules/shared/services/logging.service';

@Injectable({
    providedIn: 'root'
})
export class AppStateService extends Store<AppState> {
    private readonly _filterService = inject(FilterSpacesService);
    private readonly _router = inject(Router);
    private readonly _http = inject(HttpClient);
    private readonly _config = inject(ConfigService);
    private readonly _loggingService = inject(LoggingService);

    public get isTrafficLayerVisible() {
        if (this.stateSnapshot.selectedContext === APP_CONTEXT.HISTORY) {
            return false;
        }

        return this.stateSnapshot.mapConfig.showTrafficLayer;
    }

    public get isCityLabelsVisible() {
        return this.stateSnapshot.mapConfig?.showCityLabels ?? true;
    }


    constructor() {
        super();

        // Creates a default, empty, state. The stored state is loaded by the init method.
        this._updateState(new AppState());
    }

    public getState(): Observable<AppState> {
        return super.state$.pipe(
            distinctUntilChanged()
        );
    }

    public getPropertyState<K extends keyof AppState>(propertyKey: K): Observable<AppState | AppState[K] | Garage[]> {
        if (propertyKey === 'leftTotalWidth'
            || propertyKey === 'leftPanelIsVisible'
            || propertyKey === 'selectedGarageCodes'
        ) {
            return super.state$.pipe(
                map(state => state[propertyKey]),
                debounceTime(50),
                distinctUntilChanged()
            );
        } 

        return super.state$.pipe(
            map(state => state[propertyKey]),
            distinctUntilChanged()
        );
    }

    public getSelectedGaragesState(): Observable<Garage[]> {
        return super.state$.pipe(
            map(state => state.garages),
            map(garages => _.filter(garages, (g: Garage) => this.stateSnapshot.selectedGarageCodes?.includes(g.code))),
            filter((garages) => garages !== undefined),
            distinctUntilChanged()
        );
    }

    public getGaragesState(): Observable<Garage[]> {
        return super.state$.pipe(
            map(state => state.garages),
            filter((garage) => garage !== undefined),
            distinctUntilChanged()
        );
    }

    //#region Public functions for changing certain states
    public changeContext(context: APP_CONTEXT, reloadNavigation = true): void {
        const prevContext = this.stateSnapshot?.selectedContext;

        // same, then change to none context
        if (prevContext === context) {
            context = APP_CONTEXT.NONE;
        }

        const newState: AppState = {
            ...this.stateSnapshot,
            selectedContext: context,
            leftPanelIsVisible: true
        };

        this._updateContextAndSelectionPanelState(newState, reloadNavigation);
    }

    public setWidthForAContext(contextKey: string, width: number): void {
        const newState: AppState = {
            ...this.stateSnapshot,
            selectionOverviewWidthByContext: {
                ...this.stateSnapshot.selectionOverviewWidthByContext,
                [contextKey]: `${width}px`
            }
        };

        this._updateState(newState);
    }

    public toggleSelectionPanel(): void {
        this._setSelectionPanelVisibleValue(!this.stateSnapshot?.leftPanelIsVisible);
    }

    public showSelectionPanel(): void {
        this._setSelectionPanelVisibleValue(true);
    }

    public closeSelectionPanel(): void {
        this._setSelectionPanelVisibleValue(false);
    }

    public updateGarages(garages: Garage[]): void {
        this._updateState({
            ...this.stateSnapshot,
            garages,
        });

        this._removeMissingGaragesFromSelectedGarages();

        this._updateAndSaveState(this.stateSnapshot);
    }

    public addSelectedGarages(selectedGarageCodes: string[] = []): void {
        const garageCodes = this.stateSnapshot.selectedGarageCodes.slice(); // To make a copy to work on
        const garageCodesToAdd = _.difference(selectedGarageCodes, garageCodes);

        _.each(garageCodesToAdd, garageCode => {
            garageCodes.push(garageCode);
        });

        this._updateState({
            ...this.stateSnapshot,
            selectedGarageCodes: garageCodes
        });

        this._removeMissingGaragesFromSelectedGarages();

        this._updateAndSaveState(this.stateSnapshot);
    }

    public removeSelectedGarages(codesToRemove: string[]): void {
        let garageCodes = this.stateSnapshot.selectedGarageCodes.slice(); // To make a copy to work on
        _.each(codesToRemove, (codeToRemove) => {
            garageCodes = _.filter(garageCodes, (x) => x !== codeToRemove);
        });

        this._updateState({
            ...this.stateSnapshot,
            selectedGarageCodes: garageCodes
        });

        this._removeMissingGaragesFromSelectedGarages();

        this._updateAndSaveState(this.stateSnapshot);
    }
    //#endregion

    public setLeftTotalWidth(leftTotalWidth: number): void {
        const newState: AppState = {
            ...this.stateSnapshot,
            leftTotalWidth
        };
        this._updateState(newState);
    }

    public setPunctualityConfig(punctualityConfig: VehiclePunctualityConfig): void {
        const newState: AppState = {
            ...this.stateSnapshot,
            punctualityConfig
        };
        this._updateAndSaveState(newState);
    }

    public setMapConfig(mapConfig: UserMapConfig): void {
        const newState: AppState = {
            ...this.stateSnapshot,
            mapConfig
        };
        this._updateAndSaveState(newState);
    }

    public setWarningConfig(warningConfig: VehicleWarningConfig): void {
        const newState: AppState = {
            ...this.stateSnapshot,
            warningConfig
        };
        this._updateAndSaveState(newState);
    }

    public async setPersistedMapState(center: [number, number], zoom: number): Promise<void> {
        const mapPersistedState = this.stateSnapshot.mapPersistedState;
        mapPersistedState.center = center;
        mapPersistedState.zoom = zoom;
        const newState: AppState = {
            ...this.stateSnapshot,
            mapPersistedState
        };
        await this._updateAndSaveState(newState);
    }

    public setTrafficDeviationConfig(trafficDeviationsConfig: TrafficDeviationsConfig): void {
        this._updateAndSaveState({ ...this.stateSnapshot, trafficDeviationsConfig });
    }

    // #region Function to filter on the map
    public selectMapArea(mapArea: MapArea): void {
        this._filterService.selectMapArea(mapArea);
    }

    public clearFilterSelections(): void {
        this._filterService.clearFilterSelections();
    }
    // #endregion

    public async init(): Promise<void> {
        const storedAppState = await firstValueFrom(this._http.get<AppState>(this._config.mapApiBaseUrl + '/v1/appstate'));
        const state = new AppState();
        // Get the selected tab from the stored state and set as active if the tab exists in the tabs-list
        if (storedAppState) {
            try {
                if (storedAppState.version === state.version) {
                    state.selectedContext = storedAppState.selectedContext;
                    state.leftPanelIsVisible = storedAppState.leftPanelIsVisible;
                    state.garages = storedAppState.garages;
                    state.selectedGarageCodes = storedAppState.selectedGarageCodes;
                    state.mapConfig = new UserMapConfig(storedAppState.mapConfig);
                    state.punctualityConfig = new VehiclePunctualityConfig(storedAppState.punctualityConfig);
                    state.warningConfig = new VehicleWarningConfig(storedAppState.warningConfig);
                    state.trafficDeviationsConfig = new TrafficDeviationsConfig(storedAppState.trafficDeviationsConfig);
                    state.mapPersistedState = new MapPersistedState(storedAppState.mapPersistedState);

                    if (!state.selectedGarageCodes) {
                        state.selectedGarageCodes = [];
                    }

                    if (state.selectedContext === APP_CONTEXT.EDIT_TRAFFIC_DEVIATION_TEMPLATES) {
                        state.selectedContext = APP_CONTEXT.NONE;
                        state.leftPanelIsVisible = false;
                    }

                    this._updateState(state);
                }
            } catch (error) {
                console.error('error when parsing the stored state of the application');
            }
        }

        this._filterService.selectedValues$.pipe(
        ).subscribe(selectedValues => {
            if (!selectedValues) {
                return;
            }
            const newState: AppState = {
                ...this.stateSnapshot,
                selectedFilter: selectedValues
            };

            this._updateAndSaveState(newState);
        });
    }

    public getTrafficAreaName(garageCode: string): string {
        return _.find(this.stateSnapshot.garages, garage => garage.code === garageCode)?.trafficAreaName;
    }

    private _removeMissingGaragesFromSelectedGarages(): void {
        if (this.stateSnapshot.selectedGarageCodes) {
            const allGarageCodes = _.map(this.stateSnapshot.garages, (g) => g.code);
            // Remove selected garages the user no longer has access to.
            const filteredGarageCodes =
                _.filter(this.stateSnapshot.selectedGarageCodes, (selectedGarageCode) => allGarageCodes.includes(selectedGarageCode));

            if (filteredGarageCodes.length !== this.stateSnapshot.selectedGarageCodes.length) {
                const newState: AppState = {
                    ...this.stateSnapshot,
                    selectedGarageCodes: filteredGarageCodes
                };
                this._updateState(newState);
            }
        }
    }

    private _updateContextAndSelectionPanelState(state: AppState, reloadNavigation = true): void {
        if (state.selectedContext === APP_CONTEXT.NONE) {
            if (reloadNavigation) {
                const right0Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right0']?.segments.map(s => s.path) ?? null;
                const right1Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right1']?.segments.map(s => s.path) ?? null;
                const right2Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right2']?.segments.map(s => s.path) ?? null;
                this._router.navigate(['', {
                    outlets: { outlet0: null, outlet1: null, 'outlet-right0': right0Segments, 'outlet-right1': right1Segments, 'outlet-right2': right2Segments }
                }]);
            }
            this._setSelectionPanelVisibleValue(false, state);
        } else {
            this._setSelectionPanelVisibleValue(true, state);
        }
    }

    private _setSelectionPanelVisibleValue(value: boolean, appState?: AppState): void {
        if (this.stateSnapshot?.leftPanelIsVisible === value) {
            appState && this._updateAndSaveState(appState);
            // already the same value, do nothing
            return;
        }

        const state = appState ?? this.stateSnapshot;
        const newState: AppState = {
            ...state,
            leftPanelIsVisible: value
        };

        // if the new value is false, then the context should be none also
        if (!value) {
            newState.selectedContext = APP_CONTEXT.NONE;
        }

        this._updateAndSaveState(newState);
    }

    //#region Handles the state of the application
    private async _updateAndSaveState(state: AppState): Promise<void> {
        this._updateState(state);
        try {
            await firstValueFrom(this._http.put<any>(this._config.mapApiBaseUrl + '/v1/appstate', JSON.stringify(state)).pipe(
                catchError(error => {
                    handleError(error);
                    return of(undefined);
                })
            ));
        } catch (error) {
            if (isEmptyError(error)) {
                // No elements emitted, can be ignored
            } else {
                this._loggingService.logException(error);
                console.error('Failed to save app state:', error);
            }
        }

    }

    private _updateState(state: AppState): void {
        this.setState(state);
    }
    //#endregion
}